
import { SimulationConfig, Ball, EscapeRing, ShapeType } from '@/types';

export const HUDRenderer = {
  render: (
    ctx: CanvasRenderingContext2D,
    centerX: number,
    centerY: number,
    canvas: HTMLCanvasElement,
    dpr: number,
    balls: Ball[],
    escapeRings: EscapeRing[],
    initialEscapeRingCount: number,
    config: SimulationConfig,
    globalHue: number,
    counterPop: number,
    timeRemaining: number,
    containerRadius: number,
    bounceCount: number
  ) => {
    const rLeft = escapeRings.length;
    const isEscape = config.mode === 'ESCAPE' || config.mode === 'RHYTHMIC_ESCAPE';
    const rgbColor = `hsl(${globalHue}, 100%, 50%)`;

    const logicalWidth = canvas.width / dpr;
    const logicalHeight = canvas.height / dpr;
    const midX = logicalWidth / 2;

    // 1. Header & Footer (Screen Space) - Rendered regardless of showHUD
    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    
    const titleColor = config.rgbLighting ? rgbColor : config.titleColor;
    const titleText = config.titleText;
    
    // Header
    if (titleText) {
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      const weight = config.titleBold ? '900' : config.titleFontWeight;
      ctx.font = `${weight} ${config.titleFontSize || 24}px ${config.titleFontFamily}`;
      ctx.fillStyle = titleColor;
      ctx.globalAlpha = 1.0;
      ctx.fillText(titleText.toUpperCase(), midX, config.titleOffsetY ?? 30);
    }

    // Footer
    if (config.footerText) {
      const weight = config.footerBold ? '900' : config.footerFontWeight;
      ctx.font = `${weight} ${config.footerFontSize}px ${config.footerFontFamily}`;
      ctx.fillStyle = config.rgbLighting ? rgbColor : config.footerTextColor;
      ctx.textBaseline = 'bottom';
      ctx.textAlign = 'center';
      ctx.fillText(config.footerText.toUpperCase(), midX, logicalHeight - (config.footerOffsetY ?? 30));
    }
    ctx.restore();

    // If HUD is disabled, stop here
    if (!config.showHUD) return;

    // 2. Ring Counter (Simulation Space)
    if (isEscape && config.ringCounterPosition !== 'NONE') {
      const bFS = config.ringCounterFontSize || 180;
      const pop = counterPop;
      
      let ringDisplayVal = escapeRings.length;
      if (config.ringCounterReverse) {
        const total = initialEscapeRingCount || config.escapeRingCount;
        ringDisplayVal = total - ringDisplayVal;
      }
      
      let displayText = ringDisplayVal.toString();
      let scale = bFS * pop;
      if (config.escapeTimeChallengeMode) {
        const cR = Math.max(0, timeRemaining);
        displayText = Math.ceil(cR).toString();
      }

      ctx.save();
      ctx.shadowBlur = 0;
      let tx = centerX, ty = centerY;
      if (config.ringCounterPosition === 'TOP_CENTER') ty = centerY - containerRadius - 100;
      else if (config.ringCounterPosition === 'BOTTOM_CENTER') ty = centerY + containerRadius + 100;
      else if (config.ringCounterPosition === 'FOLLOW_BALL' && balls.length > 0) {
        tx = balls[0].pos.x; 
        ty = balls[0].pos.y + config.ringCounterOffset;
      }

      ctx.textAlign = 'center'; 
      ctx.textBaseline = 'middle'; 
      ctx.font = `${config.ringCounterFontWeight} ${scale}px ${config.ringCounterFont}`;
      ctx.globalAlpha = config.ringCounterOpacity + (pop - 1.0); 

      const finalFill = config.ringCounterUseRGB ? rgbColor : config.ringCounterColor;
      const finalStroke = config.ringCounterUseRGBBorder ? rgbColor : config.ringCounterStrokeColor;

      if (config.ringCounterShadowBlur > 0) {
        ctx.shadowBlur = config.ringCounterShadowBlur;
        ctx.shadowColor = config.ringCounterShadowColor;
      }
      if (config.ringCounterStrokeWidth > 0) {
        ctx.lineWidth = config.ringCounterStrokeWidth;
        ctx.strokeStyle = finalStroke;
        ctx.strokeText(displayText, tx, ty);
      }
      if (config.escapeTimeChallengeMode && timeRemaining < 3) ctx.fillStyle = '#ef4444';
      else ctx.fillStyle = finalFill;
      ctx.fillText(displayText, tx, ty);

      if (config.escapeTimeChallengeMode && config.ringCounterStyle === 'CENTER') {
        ctx.font = `900 ${(bFS * 0.3) * pop}px ${config.ringCounterFont}`;
        ctx.fillStyle = finalFill;
        ctx.globalAlpha = config.ringCounterOpacity * 0.7;
        ctx.fillText(`${ringDisplayVal} RINGS`, tx, ty + scale * 0.4);
      }
      ctx.restore();
    }

    // 3. Ball Size Counter (Simulation Space - Follow Ball)
    if (config.showBallSizeCounter && config.ballSizeCounterPosition === 'FOLLOW_BALL' && balls.length > 0) {
      const b = balls[0];
      const sizeText = `${config.ballSizeCounterPrefix}${b.radius.toFixed(config.ballSizeCounterDecimalPlaces)}${config.ballSizeCounterSuffix}`;
      
      ctx.save();
      ctx.font = `${config.ballSizeCounterFontWeight} ${config.ballSizeCounterFontSize}px ${config.ballSizeCounterFont}`;
      ctx.fillStyle = config.ballSizeCounterUseRGB ? rgbColor : config.ballSizeCounterColor;
      ctx.globalAlpha = config.ballSizeCounterOpacity;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      if (config.ballSizeCounterShadowBlur > 0) {
        ctx.shadowBlur = config.ballSizeCounterShadowBlur;
        ctx.shadowColor = config.ballSizeCounterShadowColor;
      }

      ctx.fillText(sizeText.toUpperCase(), b.pos.x, b.pos.y + config.ballSizeCounterOffset);
      ctx.restore();
    }

    // 4. Secondary Screen Space Elements
    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Badge (ESCAPE / RHYTHMIC_ESCAPE specific info)
    const showBadge = config.mode === 'ESCAPE' || config.mode === 'RHYTHMIC_ESCAPE';
    const hideBadgeInEscape = isEscape && (config.ringCounterStyle === 'CENTER' || config.escapeTimeChallengeMode);
    
    if (showBadge && !hideBadgeInEscape) {
      const badgeText = `${rLeft} RINGS LEFT`;
      
      ctx.font = '900 10px Inter, sans-serif';
      const metrics = ctx.measureText(badgeText);
      const paddingH = 16, paddingV = 6;
      const badgeW = metrics.width + paddingH * 2;
      const badgeH = 22;
      const bx = midX - badgeW / 2, by = (config.titleOffsetY ?? 30) + (config.titleFontSize || 24) + 10;
      
      ctx.fillStyle = config.rgbLighting ? rgbColor : (config.darkMode ? 'white' : 'black');
      ctx.beginPath();
      if ((ctx as any).roundRect) (ctx as any).roundRect(bx, by, badgeW, badgeH, 11);
      else ctx.rect(bx, by, badgeW, badgeH);
      ctx.fill();
      
      ctx.fillStyle = config.rgbLighting ? (config.darkMode ? 'black' : 'white') : (config.darkMode ? 'black' : 'white');
      ctx.textBaseline = 'middle';
      ctx.fillText(badgeText, midX, by + badgeH / 2);
    }

    // Bounce Counter (Global Screen Space)
    if (config.showBounceCounter && config.bounceCounterPosition !== 'NONE' && config.bounceCounterPosition !== 'FOLLOW_BALL') {
      const bounceText = `${config.bounceCounterPrefix}${bounceCount}${config.bounceCounterSuffix}`;
      let tx = midX;
      let ty = logicalHeight / 2;

      if (config.bounceCounterPosition === 'TOP_CENTER') {
        ty = 100 + config.bounceCounterOffset;
      } else if (config.bounceCounterPosition === 'BOTTOM_CENTER') {
        ty = logicalHeight - 100 - config.bounceCounterOffset;
      } else if (config.bounceCounterPosition === 'CENTER') {
        ty = logicalHeight / 2 + config.bounceCounterOffset;
      }

      ctx.save();
      ctx.font = `${config.bounceCounterFontWeight} ${config.bounceCounterFontSize}px ${config.bounceCounterFont}`;
      ctx.fillStyle = config.bounceCounterUseRGB ? rgbColor : config.bounceCounterColor;
      ctx.globalAlpha = config.bounceCounterOpacity;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      if (config.bounceCounterShadowBlur > 0) {
        ctx.shadowBlur = config.bounceCounterShadowBlur;
        ctx.shadowColor = config.bounceCounterShadowColor;
      }

      ctx.fillText(bounceText.toUpperCase(), tx, ty);
      ctx.restore();
    }

    // Ball Size Counter (Global Screen Space)
    if (config.showBallSizeCounter && config.ballSizeCounterPosition !== 'FOLLOW_BALL' && config.ballSizeCounterPosition !== 'NONE' && balls.length > 0) {
      const b = balls[0];
      const sizeText = `${config.ballSizeCounterPrefix}${b.radius.toFixed(config.ballSizeCounterDecimalPlaces)}${config.ballSizeCounterSuffix}`;
      
      let tx = midX;
      let ty = logicalHeight / 2;

      if (config.ballSizeCounterPosition === 'TOP_CENTER') {
        ty = 100 + config.ballSizeCounterOffset;
      } else if (config.ballSizeCounterPosition === 'BOTTOM_CENTER') {
        ty = logicalHeight - 100 - config.ballSizeCounterOffset;
      } else if (config.ballSizeCounterPosition === 'CENTER') {
        ty = logicalHeight / 2 + config.ballSizeCounterOffset;
      }

      ctx.save();
      ctx.font = `${config.ballSizeCounterFontWeight} ${config.ballSizeCounterFontSize}px ${config.ballSizeCounterFont}`;
      ctx.fillStyle = config.ballSizeCounterUseRGB ? rgbColor : config.ballSizeCounterColor;
      ctx.globalAlpha = config.ballSizeCounterOpacity;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      if (config.ballSizeCounterShadowBlur > 0) {
        ctx.shadowBlur = config.ballSizeCounterShadowBlur;
        ctx.shadowColor = config.ballSizeCounterShadowColor;
      }

      ctx.fillText(sizeText.toUpperCase(), tx, ty);
      ctx.restore();
    }
    ctx.restore();

    // 5. Bounce Counter (Simulation Space - Follow Ball)
    if (config.showBounceCounter && config.bounceCounterPosition === 'FOLLOW_BALL' && balls.length > 0) {
        const b = balls[0];
        const bounceText = `${config.bounceCounterPrefix}${bounceCount}${config.bounceCounterSuffix}`;
        
        ctx.save();
        ctx.font = `${config.bounceCounterFontWeight} ${config.bounceCounterFontSize}px ${config.bounceCounterFont}`;
        ctx.fillStyle = config.bounceCounterUseRGB ? rgbColor : config.bounceCounterColor;
        ctx.globalAlpha = config.bounceCounterOpacity;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
  
        if (config.bounceCounterShadowBlur > 0) {
          ctx.shadowBlur = config.bounceCounterShadowBlur;
          ctx.shadowColor = config.bounceCounterShadowColor;
        }
  
        ctx.fillText(bounceText.toUpperCase(), b.pos.x, b.pos.y + config.bounceCounterOffset);
        ctx.restore();
    }
  }
};
